import { initialVisualState, visualStateReducer, VisualCommandType } from './visualStateReducer';

const subscribers = new Set();

let visualSnapshot = initialVisualState;

let publicationCount = 0;
let mutationCount = 0;

const writeStyle = (element, property, value, priority = '') => {
  if (!element?.style) return false;
  const nextValue = String(value);
  if (element.style.getPropertyValue(property) === nextValue
    && element.style.getPropertyPriority(property) === priority) return false;
  element.style.setProperty(property, nextValue, priority);
  mutationCount += 1;
  return true;
};

const removeStyle = (element, property) => {
  if (!element?.style?.getPropertyValue(property)) return false;
  element.style.removeProperty(property);
  mutationCount += 1;
  return true;
};

const setAttribute = (element, name, value) => {
  if (!element || element.getAttribute(name) === String(value)) return false;
  element.setAttribute(name, String(value));
  mutationCount += 1;
  return true;
};

const removeAttribute = (element, name) => {
  if (!element?.hasAttribute(name)) return false;
  element.removeAttribute(name);
  mutationCount += 1;
  return true;
};

const toggleClass = (element, name, enabled) => {
  if (!element?.classList || element.classList.contains(name) === Boolean(enabled)) return false;
  element.classList.toggle(name, Boolean(enabled));
  mutationCount += 1;
  return true;
};

const publish = (next, theme, targetDocument = document) => {
  if (!targetDocument?.documentElement || !targetDocument?.body) return visualSnapshot;
  if (visualSnapshot.version === next.version
    && JSON.stringify(visualSnapshot.variables) === JSON.stringify(next.variables)) return visualSnapshot;
  const { documentElement, body } = targetDocument;
  publicationCount += 1;
  Object.entries(next.variables).forEach(([property, value]) => writeStyle(documentElement, property, value));
  toggleClass(body, 'opengrit-experience-body--dark', Boolean(theme.surface?.dark));
  toggleClass(body, 'opengrit-experience-body--light', !theme.surface?.dark);
  toggleClass(body, 'opengrit-experience-body--pitchdark', next.mode === 'pitchDark');
  toggleClass(body, 'opengrit-experience-body--custom', next.mode === 'custom');
  toggleClass(body, 'opengrit-experience-body--contrast-corrected', !next.accessible);
  setAttribute(body, 'data-opengrit-theme-mode', next.mode);
  setAttribute(body, 'data-opengrit-theme-accessible', next.accessible);
  setAttribute(documentElement, 'data-opengrit-theme-mode', next.mode);
  setAttribute(documentElement, 'data-opengrit-theme-accessible', next.accessible);
  setAttribute(documentElement, 'data-og-theme-version', next.themeVersion);
  setAttribute(documentElement, 'data-og-theme-commit-count', publicationCount);
  setAttribute(documentElement, 'data-og-visual-authority', 'VisualAuthority');
  setAttribute(documentElement, 'data-og-visual-version', next.version);
  visualSnapshot = next;
  subscribers.forEach((subscriber) => subscriber(visualSnapshot));
  targetDocument.defaultView?.dispatchEvent(new CustomEvent('opengrit:visual-snapshot-published', { detail: visualSnapshot }));
  return visualSnapshot;
};

const dispatch = (command, targetDocument = document) => {
  const next = visualStateReducer(visualSnapshot, command);
  if (next === visualSnapshot) return visualSnapshot;
  return publish(next, command.theme || {}, targetDocument);
};

export const visualAuthority = Object.freeze({
  dispatch,
  publishTheme: (theme, targetDocument) => dispatch({ type: VisualCommandType.COMMIT_THEME, theme }, targetDocument),
  requestStyles: (source, element, styles = {}, priority = '') => {
    Object.entries(styles).forEach(([property, value]) => {
      if (value == null || value === '') removeStyle(element, property);
      else writeStyle(element, property, value, priority);
    });
  },
  requestAttributes: (source, element, attributes = {}) => {
    Object.entries(attributes).forEach(([name, value]) => {
      if (value == null || value === false) removeAttribute(element, name);
      else setAttribute(element, name, value);
    });
  },
  requestClass: (source, element, name, enabled) => toggleClass(element, name, enabled),
  getSnapshot: () => visualSnapshot,
  getDiagnostics: () => ({
    authority: 'VisualAuthority',
    publisher: visualSnapshot.publisher,
    visualVersion: visualSnapshot.version,
    reducerVersion: visualSnapshot.reducerVersion,
    visualCommit: publicationCount,
    themeVersion: visualSnapshot.themeVersion,
    publicationCount,
    mutationCount,
    consumers: [
      'GlobalThemeBridge', 'StorefrontExperienceAdmin', 'LivePageEdit',
      'LocalSurfaceContrastEngine', 'TypographyEngine', 'ImageRegionEngine',
      'SemanticSurfaceEngine', 'ThemeAwareAsset', 'HeroSafeZoneEngine',
    ],
    subscribers: subscribers.size,
  }),
  subscribe: (subscriber) => {
    subscribers.add(subscriber);
    subscriber(visualSnapshot);
    return () => subscribers.delete(subscriber);
  },
});

export default visualAuthority;
